import React, { useState } from 'react';
import { ShieldCheck, Lock, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { cognitoConfig } from '../../config/auth';

export const LoginScreen: React.FC = () => {
  const { login, authError, clearError, isConfigured } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    clearError();
    setIsSubmitting(true);

    try {
      await login(username.trim(), password);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const combinedError = localError || authError;

  return (
    <div className="potentia-auth-shell">
      <div className="potentia-auth-ambient" aria-hidden="true">
        <span className="potentia-auth-orb orb-one" />
        <span className="potentia-auth-orb orb-two" />
        <span className="potentia-auth-orb orb-three" />
      </div>

      <div className="potentia-auth-content">
        <div className="potentia-auth-header">
          <div className="potentia-auth-brand">
            <div className="potentia-auth-logo">
              <img
                src="https://cdn.prod.website-files.com/669c1487641a968531362ff6/669dd358bc56adba53d91680_ecmx_icon.png"
                alt="EcoMetricx"
              />
            </div>
            <div>
              <div className="potentia-auth-brand-name">
                <span className="potentia-auth-brand-accent">Eco</span>Metricx
                <span className="potentia-auth-brand-tag">Potentia</span>
              </div>
              <p className="potentia-auth-brand-subtitle">Secure access to the agentic analytics cockpit</p>
            </div>
          </div>
          <div className="potentia-auth-meta">
            <ShieldCheck size={16} />
            <span>Potentia-prod Cognito</span>
          </div>
        </div>

        <div className="potentia-auth-grid">
          <div className="panel-surface potentia-auth-panel">
            <div className="potentia-auth-eyebrow">
              <span className="potentia-auth-status-dot" />
              Potentia Natural Language Interface
            </div>
            <h1 className="potentia-auth-title">
              Raise the competency of every analyst with verified NLP-to-SQL output.
            </h1>
            <div className="accent-chip">
              <span className="potentia-auth-chip-dot" />
              Demo access enabled
            </div>
            <p className="potentia-auth-description">
              Use your Potentia-prod Cognito credentials. New resources should follow the
              {` "${cognitoConfig.resourcePrefix}-<resource>"`} naming convention to stay aligned with infra.
            </p>
            <div className="potentia-auth-list">
              {[
                'Agentic routing + schema-aware prompts deliver higher quality SQL.',
                'Guardrailed execution and policy validation keep outputs trustworthy.',
                'Auto-generated graphs with chat-native refinement and overrides.',
                'Insight narration and saved graphs keep teams aligned on outcomes.',
              ].map((item) => (
                <div key={item} className="potentia-auth-list-item">
                  <span className="potentia-auth-list-icon">
                    <CheckCircle2 size={16} />
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="panel-strong potentia-auth-card">
            <div className="potentia-auth-form-title">
              <Lock size={16} />
              <span>Sign in</span>
            </div>

            {!isConfigured && (
              <div className="potentia-auth-warning">
                Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID for your Potentia-prod user pool/client in the
                environment to enable login.
              </div>
            )}

            {combinedError && (
              <div className="potentia-auth-error">
                <AlertCircle size={16} />
                <span>{combinedError}</span>
              </div>
            )}

            <form className="potentia-auth-form" onSubmit={handleSubmit}>
              <label className="potentia-auth-label">
                <span>Username or Email</span>
                <input
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className="input-shell potentia-auth-input"
                  placeholder="you@company.com"
                  autoComplete="username"
                  required
                />
              </label>

              <label className="potentia-auth-label">
                <span>Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="input-shell potentia-auth-input"
                  placeholder="********"
                  autoComplete="current-password"
                  required
                />
              </label>

              <button
                type="submit"
                disabled={!username || !password || isSubmitting || !isConfigured}
                className="potentia-auth-button"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
                <ArrowRight size={16} />
              </button>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};
